import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Button } from 'reactstrap';
import { VpSelect } from '../../ui/VpSelect';

export interface SplitRowDialogOperator { id: number; label: string; }

export interface SplitRowDialogProps {
  isOpen: boolean;
  qtyTotal: number;
  itemLabel: string;
  operators: SplitRowDialogOperator[];
  initialParts: Array<{ operator_user_id: number; qty: number }>;
  onSubmit: (parts: Array<{ operator_user_id: number; qty: number }>) => void;
  onCancel: () => void;
}

interface DraftRow { operator_user_id: number | null; qty: number; }

export function SplitRowDialog({
  isOpen,
  qtyTotal,
  itemLabel,
  operators,
  initialParts,
  onSubmit,
  onCancel,
}: SplitRowDialogProps) {
  const { t } = useTranslation('picklist');
  const [rows, setRows] = useState<DraftRow[]>([]);

  useEffect(() => {
    if (!isOpen) return;
    setRows(initialParts.length > 0
      ? initialParts.map((p) => ({ operator_user_id: p.operator_user_id, qty: p.qty }))
      : [{ operator_user_id: null, qty: qtyTotal }]);
  }, [isOpen, qtyTotal, initialParts]);

  if (!isOpen) return null;

  const sum = rows.reduce((s, r) => s + (Number.isFinite(r.qty) ? r.qty : 0), 0);
  const remaining = qtyTotal - sum;
  const allComplete = rows.every((r) => r.operator_user_id !== null && r.qty > 0);
  const canSubmit = sum === qtyTotal && allComplete && rows.length > 0;

  function updateRow(idx: number, patch: Partial<DraftRow>): void {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }

  function addRow(): void {
    setRows((prev) => [...prev, { operator_user_id: null, qty: Math.max(0, qtyTotal - sum) }]);
  }

  function removeRow(idx: number): void {
    setRows((prev) => prev.filter((_, i) => i !== idx));
  }

  function submit(): void {
    if (!canSubmit) return;
    onSubmit(rows.map((r) => ({ operator_user_id: r.operator_user_id!, qty: r.qty })));
  }

  function clearAll(): void {
    onSubmit([]);
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('wizard.manual.split_row_dialog.aria_label', { label: itemLabel })}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--vp-bg)',
          padding: 16,
          borderRadius: 'var(--vp-r-md)',
          minWidth: 420,
          maxWidth: 560,
          boxShadow: 'var(--vp-shadow-lg)',
        }}
      >
        <h5 className="fs-15 mb-2">
          {t('wizard.manual.split_row_dialog.title', { label: itemLabel })}
        </h5>
        <p className="small text-muted mb-3">
          {t('wizard.manual.split_row_dialog.subtitle', { qty: qtyTotal })}
        </p>

        <div className="d-flex flex-column gap-2">
          {rows.map((row, idx) => (
            <div key={idx} className="d-flex align-items-center gap-2">
              <div style={{ flex: 1 }}>
                <VpSelect
                  ariaLabel={t('wizard.manual.split_row_dialog.operator_label', { n: idx + 1 })}
                  placeholder={t('wizard.manual.split_row_dialog.operator_placeholder')}
                  options={operators.map((op) => ({ value: op.id, label: op.label }))}
                  value={row.operator_user_id}
                  onChange={(v) => updateRow(idx, {
                    operator_user_id: v === null ? null : Number(v),
                  })}
                />
              </div>
              <Input
                aria-label={t('wizard.manual.split_row_dialog.qty_label', { n: idx + 1 })}
                type="number"
                min={1}
                max={qtyTotal}
                value={row.qty}
                onChange={(e) => updateRow(idx, { qty: Number(e.target.value) })}
                style={{ width: 90 }}
              />
              <span className="text-muted" style={{ fontSize: 'var(--vp-text-xs)' }}>{t('wizard.manual.split_row_dialog.qty_unit')}</span>
              <Button
                type="button" color="light" size="sm"
                onClick={() => removeRow(idx)}
                aria-label={t('wizard.manual.split_row_dialog.remove_row_aria', { n: idx + 1 })}
                disabled={rows.length === 1}
              >
                ✕
              </Button>
            </div>
          ))}
        </div>

        <div className="mt-3 d-flex align-items-center gap-2">
          <Button type="button" color="light" size="sm" onClick={addRow}>
            {t('wizard.manual.split_row_dialog.add_operator')}
          </Button>
          <span
            className="fw-semibold ms-auto"
            style={{
              fontSize: 'var(--vp-text-sm)',
              color: sum === qtyTotal ? 'var(--vp-success)' : (sum > qtyTotal ? 'var(--vp-danger)' : 'var(--vp-text-2)'),
            }}
          >
            {t('wizard.manual.split_row_dialog.total_prefix', { sum, total: qtyTotal })}
            {' '}
            {sum === qtyTotal
              ? t('wizard.manual.split_row_dialog.total_ok_suffix')
              : (remaining > 0
                  ? t('wizard.manual.split_row_dialog.total_missing', { n: remaining })
                  : t('wizard.manual.split_row_dialog.total_excess', { n: -remaining }))}
          </span>
        </div>

        <div className="mt-3 d-flex gap-2 justify-content-end">
          <Button type="button" color="light" onClick={clearAll}>
            {t('wizard.manual.split_row_dialog.cancel_split')}
          </Button>
          <Button type="button" color="light" onClick={onCancel}>
            {t('wizard.manual.split_row_dialog.close')}
          </Button>
          <Button
            type="button" color="primary"
            onClick={submit}
            disabled={!canSubmit}
          >
            {t('wizard.manual.split_row_dialog.confirm')}
          </Button>
        </div>
      </div>
    </div>
  );
}
